import { useEffect, useRef, useState } from 'react';
import type { DietaryConcernReason, Dish, DishExplanation } from '../../shared/menu';
import { dishHash } from '../../shared/menu';
import { explainDish, ApiError } from '../lib/api';
import { loadExplanationLocal, saveExplanationLocal } from '../lib/explainCache';

interface DishDetailsPanelProps {
  dish: Dish;
  menuId: string | undefined;
  targetLanguage: string;
  dietaryReason: DietaryConcernReason | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'loaded'; explanation: DishExplanation; cached: boolean }
  | { kind: 'error'; message: string }
  | { kind: 'offline' };

// "What is this dish?" — inline accordion that expands inside DishSheet.
// Lazy-loads the explanation on mount, aborts in-flight fetch on unmount,
// checks IDB cache first, gates staleness via dishHash. Renders inline so
// the user keeps the ordering controls visible while reading — no portal,
// no scroll-lock, no separate screen.
//
// Content is declarative informational context (what this is, what to
// expect, honest cultural/food-safety caveats). It is NOT the allergy-
// safety layer — that's handled by the strict-tag + hand-off flow.
export function DishDetailsPanel({ dish, menuId, targetLanguage, dietaryReason }: DishDetailsPanelProps) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [showSpinner, setShowSpinner] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const expectedHash = dishHash(dish);

  useEffect(() => {
    if (!menuId) {
      setState({ kind: 'error', message: 'Menu unavailable.' });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;

    const skeletonTimer = setTimeout(() => {
      if (!cancelled) setShowSpinner(true);
    }, 200);

    (async () => {
      try {
        const cached = await loadExplanationLocal(menuId, dish.id, targetLanguage);
        if (cached && cached.dishHash === expectedHash) {
          if (cancelled) return;
          clearTimeout(skeletonTimer);
          setShowSpinner(false);
          setState({ kind: 'loaded', explanation: cached.explanation, cached: true });
          return;
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          if (cancelled) return;
          clearTimeout(skeletonTimer);
          setShowSpinner(false);
          setState({ kind: 'offline' });
          return;
        }
        const result = await explainDish({
          menuId,
          dishId: dish.id,
          targetLanguage,
          dietaryReason,
          signal: controller.signal,
        });
        if (cancelled) return;
        clearTimeout(skeletonTimer);
        setShowSpinner(false);
        await saveExplanationLocal(menuId, dish.id, targetLanguage, result.explanation, expectedHash);
        setState({ kind: 'loaded', explanation: result.explanation, cached: result.cached });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        clearTimeout(skeletonTimer);
        setShowSpinner(false);
        const message = err instanceof ApiError ? err.message : 'Could not load details.';
        setState({ kind: 'error', message });
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(skeletonTimer);
      controller.abort();
    };
  }, [menuId, dish.id, targetLanguage, dietaryReason, expectedHash]);

  return (
    <div
      id="dish-details-panel"
      role="region"
      aria-label="What is this dish?"
      style={{ paddingTop: 12 }}
    >
      {state.kind === 'loading' && showSpinner && (
        <p
          role="status"
          aria-live="polite"
          style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}
        >
          Loading…
        </p>
      )}

      {state.kind === 'offline' && (
        <p role="note" style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          Offline — load this dish once online and the details work offline after.
        </p>
      )}

      {state.kind === 'error' && (
        <div
          role="alert"
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            background: 'rgba(196,51,43,0.06)',
            border: '1px solid rgba(196,51,43,0.25)',
            color: 'var(--color-primary)',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {state.message}
        </div>
      )}

      {state.kind === 'loaded' && <LoadedBody explanation={state.explanation} />}
    </div>
  );
}

function LoadedBody({ explanation }: { explanation: DishExplanation }) {
  const flowingIdentity = [explanation.identity, explanation.prep, explanation.experience]
    .filter((s) => s && s.trim().length > 0)
    .join(' ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {explanation.hiddenWarnings.length > 0 && (
        <div
          style={{
            padding: '12px 14px',
            background: 'rgba(196,51,43,0.10)',
            border: '1.5px solid rgba(196,51,43,0.40)',
            borderRadius: 10,
          }}
        >
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#C4332B',
            }}
          >
            Heads up
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, lineHeight: 1.45 }}>
            {explanation.hiddenWarnings.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      )}

      {flowingIdentity && (
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{flowingIdentity}</p>
      )}

      {explanation.caveats.length > 0 && (
        <div>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-text-secondary)',
            }}
          >
            Things to know
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, lineHeight: 1.45 }}>
            {explanation.caveats.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      )}

      {explanation.proTip && (
        <p style={{ margin: 0, fontSize: 13, fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
          {explanation.proTip}
        </p>
      )}
    </div>
  );
}
